import { EditIcon } from "@chakra-ui/icons";
import {
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    Badge,
    Box,
    Button,
    Center,
    Heading,
    HStack,
    SimpleGrid,
    Spinner,
    Stack,
    Text,
    useDisclosure,
    useToast,
} from "@chakra-ui/react";
import { addMonths, format } from "date-fns";
import { useEffect, useRef, useState } from "react";
import { PiTrash } from "react-icons/pi";
import { useParams } from "react-router-dom";
import { cancelSubscription, fetchSubscriptionDetails } from "../../api";
import AccessDenied from "../../components/AccessDenied";
import { hasPermission } from "../../Utils";
import EditSubscriptionModal from './EditSubscriptionModal';

export default function SubscriptionDetailsPage() {
    const { id } = useParams();
    const toast = useToast();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
    const { isOpen: isCancelOpen, onOpen: onCancelOpen, onClose: onCancelClose } = useDisclosure();
    const cancelRef = useRef();
    const [isCancelling, setIsCancelling] = useState(false);


    useEffect(() => {
        loadData();
    }, [id]);

    const loadData = async () => {
        setLoading(true);
        const res = await fetchSubscriptionDetails(id);
        if (res.success) {
            setData(res.data?.subscription || null);
        }
        setLoading(false);
    };

    if (!hasPermission("viewSubscriptions")) {
        return <AccessDenied msg="You don't have permission to view subscription details" />;
    }

    if (loading) {
        return (
            <Center mt="20%">
                <Spinner size="xl" />
            </Center>
        );
    }

    if (!data) {
        return (
            <Center mt="20%">
                <Text>Failed to load subscription details.</Text>
            </Center>
        );
    }

    const interval = data?.paymentSchedule?.interval || {};
    const startDate = new Date(data?.paymentSchedule?.startDate);
    const total = data?.paymentSchedule?.totalOccurrences || 0;
    const trial = data?.paymentSchedule?.trialOccurrences || 0;

    const handleCancel = async () => {
        setIsCancelling(true);
        const res = await cancelSubscription(id);
        setIsCancelling(false);
        if (res.success) {
            onCancelClose();
            loadData();
        } else {
            toast({
                title: 'Cancel Failed',
                description: res.error,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };


    return (
        <Box p={8}>
            <HStack spacing={6} mb={6}>
                <Box>
                    <Heading size="lg">Subscription #{id}</Heading>
                    <Box mt={5}>
                        <Badge colorScheme={data.status === 'active' ? 'green' : 'red'}>
                            {data.status.toUpperCase()}
                        </Badge>
                        {data.status === 'active' &&
                            <>
                                {hasPermission("updateSubscriptions") &&
                                    <Button ml={5} leftIcon={<EditIcon />} size={"xs"} onClick={onEditOpen} colorScheme="blue">Edit Subscription</Button>
                                }
                                {hasPermission("cancelSubscriptions") &&
                                    <Button ml={5} leftIcon={<PiTrash />} size={"xs"} onClick={onCancelOpen} colorScheme="red">Cancel Subscription</Button>
                                }
                            </>
                        }
                    </Box>
                </Box>

            </HStack>

            <SimpleGrid templateColumns={{ base: '1fr', md: '3fr 1fr' }} spacing={8}>
                <Box>
                    <Box mb={8} p={6} borderWidth="1px" borderRadius="lg">
                        <Heading size="md" mb={4}>Subscription Details</Heading>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                            <Text><b>Name:</b> {data.name || "N/A"}</Text>
                            <Text>
                                <b>Start Date:</b>{" "}
                                {!isNaN(startDate) ? format(startDate, "MMMM dd, yyyy") : "N/A"}
                            </Text>
                            <Text><b>Billing Interval:</b> {interval.length} {interval.unit}</Text>
                            <Text><b>Total Occurrences:</b> {total}</Text>
                            <Text><b>Trial Occurrences:</b> {trial}</Text>
                            <Text><b>Amount per Cycle:</b> ${data.amount}</Text>
                            <Text><b>Trial Amount:</b> ${data.trialAmount}</Text>
                        </SimpleGrid>
                    </Box>

                    <Box mb={8} p={6} borderWidth="1px" borderRadius="lg">
                        <Heading size="md" mb={4}>Customer Info</Heading>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                            <Text><b>Email:</b> {data.profile?.email}</Text>
                            <Text>
                                <b>Name:</b> {data.profile?.paymentProfile?.billTo?.firstName}{" "}
                                {data.profile?.paymentProfile?.billTo?.lastName}
                            </Text>
                            <Text colSpan={2}>
                                <b>Address:</b>{" "}
                                {data.profile?.paymentProfile?.billTo?.address},{" "}
                                {data.profile?.paymentProfile?.billTo?.city},{" "}
                                {data.profile?.paymentProfile?.billTo?.state}{" "}
                                {data.profile?.paymentProfile?.billTo?.zip},{" "}
                                {data.profile?.paymentProfile?.billTo?.country}
                            </Text>
                        </SimpleGrid>
                    </Box>

                    <Box mb={8} p={6} borderWidth="1px" borderRadius="lg">
                        <Heading size="md" mb={4}>Payment Info</Heading>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                            <Text><b>Card:</b> {data.profile?.paymentProfile?.payment?.creditCard?.cardNumber}</Text>
                            <Text><b>Customer Profile ID:</b> {data.profile?.customerProfileId}</Text>
                            <Text><b>Payment Profile ID:</b> {data.profile?.paymentProfile?.customerPaymentProfileId}</Text>
                        </SimpleGrid>
                    </Box>
                </Box>

                <Box p={6} borderWidth="1px" borderRadius="lg">
                    <Heading size="md" mb={4}>Expected Billing Dates</Heading>
                    {total > 0 ? (
                        <Stack spacing={2}>
                            {[...Array(total)].map((_, i) => (
                                <Text key={i}>{format(addMonths(startDate, i), "MMMM dd, yyyy")}</Text>
                            ))}
                        </Stack>
                    ) : (
                        <Text>No billing dates found</Text>
                    )}
                </Box>
            </SimpleGrid>


            <EditSubscriptionModal
                isOpen={isEditOpen}
                onClose={onEditClose}
                subscription={data}
                onSuccess={loadData}
                subId={id}
            />

            <AlertDialog
                isOpen={isCancelOpen}
                leastDestructiveRef={cancelRef}
                onClose={onCancelClose}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader>Cancel Subscription</AlertDialogHeader>
                        <AlertDialogBody>
                            Are you sure you want to cancel this subscription? This action cannot be undone.
                        </AlertDialogBody>

                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={onCancelClose}>
                                No
                            </Button>
                            <Button
                                colorScheme="red"
                                onClick={handleCancel}
                                ml={3}
                                isLoading={isCancelling}
                            >
                                Yes, Cancel
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>

        </Box>
    );
}
